import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import {
  LayoutDashboard,
  Type,
  ScrollText,
  Shield,
  Image as ImageIcon,
  User,
  Link2,
  Users,
  LogOut,
  Crown,
  ExternalLink,
  Folder,
  Sparkles,
  Lock,
} from "lucide-react";

const SECTIONS = [
  { to: "/admin", label: "Panoramica", icon: LayoutDashboard, exact: true },
  { to: "/admin/hero", label: "Hero", icon: Type },
  { to: "/admin/lore", label: "Lore", icon: ScrollText },
  { to: "/admin/universe", label: "Universo (Saga)", icon: Sparkles },
  { to: "/admin/features", label: "Features", icon: Type },
  { to: "/admin/factions", label: "Forze dell'Ordine", icon: Shield },
  { to: "/admin/chapters", label: "Regolamento (capitoli)", icon: ScrollText },
  { to: "/admin/rules-meta", label: "Pagina Regolamento", icon: ScrollText },
  { to: "/admin/gallery", label: "Galleria", icon: ImageIcon },
  { to: "/admin/character", label: "Character Showcase", icon: User },
  { to: "/admin/media", label: "Media Library", icon: Folder },
  { to: "/admin/links", label: "Link & Social", icon: Link2 },
  { to: "/admin/bible", label: "Bibbia (Staff)", icon: Lock, accent: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background:
          "radial-gradient(80% 60% at 100% 0%, rgba(212,175,55,0.06), transparent 55%), #08080C",
        color: "#F5F5F5",
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside
          className="hidden lg:flex flex-col p-6 sticky top-0 h-screen overflow-y-auto"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,20,27,0.96), rgba(11,11,15,0.98))",
            borderRight: "1px solid rgba(212,175,55,0.16)",
          }}
        >
          <div className="flex items-center gap-3 mb-9">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(180deg, rgba(212,175,55,0.16), rgba(212,175,55,0.04))",
                border: "1px solid rgba(212,175,55,0.4)",
                boxShadow: "0 0 22px -6px rgba(212,175,55,0.55)",
              }}
            >
              <Crown size={16} className="text-[#D4AF37]" strokeWidth={1.6} />
            </div>
            <div>
              <p className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#D4AF37]">
                Admin Console
              </p>
              <p className="font-display text-base uppercase text-[#F5F5F5] tracking-wide leading-none mt-1">
                PURELIFE CMS
              </p>
            </div>
          </div>

          <nav className="flex flex-col gap-1 flex-1">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <NavLink
                  key={s.to}
                  to={s.to}
                  end={s.exact}
                  data-testid={`admin-nav-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                      isActive
                        ? "bg-[#D4AF37]/10 border border-[#D4AF37]/35 text-[#F5F5F5]"
                        : "border border-transparent text-[#B6B6BE] hover:text-[#F5F5F5] hover:bg-white/[0.03]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          isActive
                            ? "bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37]"
                            : "border border-white/8 text-[#B6B6BE] group-hover:border-[#00D4FF]/35"
                        }`}
                      >
                        <Icon size={14} strokeWidth={1.7} />
                      </span>
                      <span className="font-medium">{s.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}

            {user?.role === "owner" && (
              <NavLink
                to="/admin/users"
                data-testid="admin-nav-users"
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all mt-3 ${
                    isActive
                      ? "bg-[#00D4FF]/10 border border-[#00D4FF]/35 text-[#F5F5F5]"
                      : "border border-transparent text-[#B6B6BE] hover:text-[#F5F5F5] hover:bg-white/[0.03]"
                  }`
                }
              >
                <span className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/8 group-hover:border-[#00D4FF]/35">
                  <Users size={14} strokeWidth={1.7} />
                </span>
                <span className="font-medium">Gestione Admin</span>
              </NavLink>
            )}
          </nav>

          <div className="mt-6 pt-5 border-t border-white/[0.06]">
            <div className="px-3.5 py-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02]">
              <p className="text-[9px] uppercase tracking-[0.28em] text-[#D4AF37]">
                {user?.role === "owner" ? "Owner" : "Editor"}
              </p>
              <p className="text-sm font-semibold text-[#F5F5F5] mt-1 truncate">
                {user?.name || "Admin"}
              </p>
              <p className="text-[11px] text-[#B6B6BE] truncate">{user?.email}</p>
            </div>

            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center justify-center gap-2 w-full rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em] border border-white/10 text-[#B6B6BE] hover:text-[#F5F5F5] hover:border-white/25 transition-colors"
            >
              <ExternalLink size={12} />
              Vai al sito
            </a>

            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="mt-2 flex items-center justify-center gap-2 w-full rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em] border border-[#E26B6B]/35 text-[#E26B6B] hover:bg-[#E26B6B]/10 transition-colors"
            >
              <LogOut size={12} />
              Esci
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 px-5 py-4 flex items-center justify-between" style={{ background: "rgba(8,8,12,0.92)", borderBottom: "1px solid rgba(212,175,55,0.18)", backdropFilter: "blur(12px)" }}>
          <div className="flex items-center gap-2">
            <Crown size={16} className="text-[#D4AF37]" />
            <span className="font-display text-sm uppercase tracking-wide">PURELIFE CMS</span>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-mobile"
            className="text-xs uppercase tracking-[0.18em] text-[#E26B6B] flex items-center gap-1.5"
          >
            <LogOut size={12} />
            Esci
          </button>
        </header>

        {/* Main */}
        <main className="px-5 sm:px-10 py-8 sm:py-12 max-w-[1200px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
